import React from "react";
import { Divider, Stack } from "@mui/material";
import PropTypes from "prop-types";
import SoftBox from "../../../components/SoftBox";
import SoftSelect from "../../../components/SoftSelect";
import SoftTypography from "../../../components/SoftTypography";
import { cashierThemePresets } from "../../../constants/cashierThemePresets";
import ArrowBackIosNewRoundedIcon from "@mui/icons-material/ArrowBackIosNewRounded";
import CreditCardOutlinedIcon from "@mui/icons-material/CreditCardOutlined";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import FlagOutlinedIcon from "@mui/icons-material/FlagOutlined";
import StarOutlineRoundedIcon from "@mui/icons-material/StarOutlineRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import LimeCashierPreview from "./LimeCashierPreview";

const previewText = {
  title: "Select Method",
  optionA: "Credit Card",
  optionB: "Crypto",
  cta: "Pay 554 EUR",
  saveCard: "Save details for next time",
  poweredBy: "Pay Securely with",
};

const normalizeLogoUrl = (url) => {
  if (!url || typeof url !== "string") {
    return null;
  }

  const trimmedUrl = url.trim();
  if (!trimmedUrl) {
    return null;
  }

  if (trimmedUrl.startsWith("blob:") || trimmedUrl.startsWith("/")) {
    return trimmedUrl;
  }

  if (trimmedUrl.startsWith("//")) {
    const protocol = typeof window !== "undefined" ? window.location.protocol : "https:";
    return `${protocol}${trimmedUrl}`;
  }

  try {
    const parsedUrl = new URL(trimmedUrl);
    if (parsedUrl.hostname === "host.docker.internal" && typeof window !== "undefined") {
      parsedUrl.hostname = window.location.hostname || "localhost";
    }
    return parsedUrl.toString();
  } catch {
    return trimmedUrl;
  }
};

function PresetPreview({ preset, brandLogoUrl }) {
  const optionBaseSx = {
    minHeight: 44,
    borderRadius: "999px",
    border: `1px solid ${preset.preview.optionBorderColor}`,
    background: preset.preview.optionBackground,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    px: 1.5,
    color: preset.preview.textColor,
    fontSize: "0.875rem",
    fontWeight: 600,
  };

  const textMuted = preset.preview.subTextColor || "rgba(0,0,0,0.6)";
  const resolvedLogoUrl = normalizeLogoUrl(brandLogoUrl);
  const [logoLoadError, setLogoLoadError] = React.useState(false);
  const shouldShowLogo = Boolean(resolvedLogoUrl) && !logoLoadError;

  React.useEffect(() => {
    setLogoLoadError(false);
  }, [resolvedLogoUrl]);

  const poweredByLogoSrc =
    preset.id === "lotto-2" || preset.id === "slick"
      ? "/presets/crypto/cyberpay-logo-light.svg"
      : "/presets/crypto/cyberpay-logo.svg";

  return (
    <SoftBox
      sx={{
        width: { xs: "100%", md: 410 },
        borderRadius: 3,
        overflow: "hidden",
        boxShadow: preset.preview.panelShadow || "0 12px 24px rgba(0,0,0,0.08)",
        border: "1px solid rgba(0,0,0,0.06)",
        background: preset.preview.appBackground,
      }}
    >
      <SoftBox
        sx={{
          px: 2,
          pt: 1.5,
          pb: 4.5,
          background: preset.preview.headerBackground,
          color: preset.preview.headerTextColor || preset.preview.textColor,
          position: "relative",
          minHeight: 120,
          overflow: "hidden",
        }}
      >
        {preset.preview.bannerImageUrl && (
          <SoftBox
            component="img"
            src={preset.preview.bannerImageUrl}
            alt={`${preset.label} background`}
            sx={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              objectFit: "cover",
              opacity: preset.id === "gaming" ? 0.65 : 0.28,
            }}
          />
        )}
        <SoftBox
          sx={{
            position: "absolute",
            inset: 0,
            background:
              preset.id === "gaming"
                ? "linear-gradient(180deg, rgba(0,0,0,0.05), rgba(0,0,0,0.55))"
                : "none",
          }}
        />
        {preset.preview.showAnnouncement && (
          <SoftBox
            sx={{
              position: "relative",
              zIndex: 2,
              mx: "auto",
              mb: 1.25,
              width: "fit-content",
              px: 2,
              py: 0.5,
              borderRadius: "999px",
              background: preset.preview.announcementBackground,
              color: preset.preview.announcementTextColor,
              fontSize: "0.75rem",
              fontWeight: 700,
            }}
          >
            {preset.preview.announcementText}
          </SoftBox>
        )}
        <SoftTypography
          sx={{
            color: preset.preview.headerTextColor || preset.preview.textColor,
            fontWeight: 700,
            textAlign: "center",
            fontSize: "1.05rem",
            letterSpacing: "0.03em",
            position: "relative",
            zIndex: 2,
          }}
        >
          {preset.label.toUpperCase()}
        </SoftTypography>
      </SoftBox>

      <SoftBox
        sx={{
          p: 2.5,
          pt: shouldShowLogo ? 4.5 : 2.5,
          background: preset.preview.bodyBackground,
          position: "relative",
        }}
      >
        {shouldShowLogo && (
          <SoftBox
            sx={{
              position: "absolute",
              top: -34,
              left: "50%",
              transform: "translateX(-50%)",
              width: 68,
              height: 68,
              borderRadius: "50%",
              background: "#FFFFFF",
              border: "2px solid rgba(0, 0, 0, 0.1)",
              boxShadow: "0 6px 14px rgba(0,0,0,0.2)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              overflow: "hidden",
            }}
          >
            <SoftBox
              component="img"
              src={resolvedLogoUrl}
              alt={`${preset.label} logo`}
              onError={() => setLogoLoadError(true)}
              sx={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
          </SoftBox>
        )}

        <SoftBox
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 1.25,
          }}
        >
          <SoftTypography
            sx={{ color: preset.preview.textColor, fontWeight: 700, fontSize: "1.05rem" }}
          >
            {previewText.title}
          </SoftTypography>
          <SoftBox
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.6,
              color: preset.preview.textColor,
            }}
          >
            <ArrowBackIosNewRoundedIcon sx={{ fontSize: "0.8rem" }} />
            <SoftTypography sx={{ color: preset.preview.textColor, fontSize: "0.9rem" }}>
              Back
            </SoftTypography>
          </SoftBox>
        </SoftBox>

        <Stack spacing={1}>
          <SoftBox sx={optionBaseSx}>
            <SoftBox sx={{ display: "flex", alignItems: "center", gap: 0.9 }}>
              <CreditCardOutlinedIcon
                sx={{ fontSize: "1.15rem", color: preset.preview.textColor }}
              />
              <SoftTypography
                sx={{ color: preset.preview.textColor, fontSize: "0.94rem", fontWeight: 600 }}
              >
                {previewText.optionA}
              </SoftTypography>
            </SoftBox>
          </SoftBox>
          <SoftBox
            sx={{
              ...optionBaseSx,
              border: `2px solid ${preset.preview.optionActiveBorderColor}`,
            }}
          >
            <SoftBox sx={{ display: "flex", alignItems: "center", gap: 0.9 }}>
              <CreditCardOutlinedIcon
                sx={{ fontSize: "1.15rem", color: preset.preview.textColor }}
              />
              <SoftTypography
                sx={{ color: preset.preview.textColor, fontSize: "0.94rem", fontWeight: 600 }}
              >
                {previewText.optionB}
              </SoftTypography>
            </SoftBox>
            <SoftBox
              sx={{
                width: 28,
                height: 28,
                borderRadius: "50%",
                background: preset.preview.optionActiveBorderColor,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <ArrowForwardRoundedIcon sx={{ color: "#fff", fontSize: "1rem" }} />
            </SoftBox>
          </SoftBox>
        </Stack>

        <SoftBox
          sx={{
            mt: 1.2,
            borderRadius: 2.5,
            background: preset.preview.cardBackground,
            border: `1px solid ${
              preset.preview.cardBorderColor || preset.preview.optionBorderColor
            }`,
            p: 1.5,
          }}
        >
          <SoftTypography
            sx={{
              fontSize: "0.62rem",
              letterSpacing: "0.08em",
              color: preset.preview.cardTextColor,
            }}
          >
            CARD NUMBER
          </SoftTypography>
          <SoftTypography
            sx={{ fontSize: "1.25rem", fontWeight: 700, color: preset.preview.cardTextColor }}
          >
            4111 1111 1111 1111
          </SoftTypography>
          <SoftBox sx={{ display: "flex", justifyContent: "space-between", mt: 1.2 }}>
            <SoftBox>
              <SoftTypography sx={{ fontSize: "0.62rem", color: preset.preview.cardTextColor }}>
                EXPIRY
              </SoftTypography>
              <SoftTypography
                sx={{ fontSize: "0.95rem", fontWeight: 700, color: preset.preview.cardTextColor }}
              >
                01/30
              </SoftTypography>
            </SoftBox>
            <SoftBox>
              <SoftTypography sx={{ fontSize: "0.62rem", color: preset.preview.cardTextColor }}>
                CVV
              </SoftTypography>
              <SoftTypography
                sx={{ fontSize: "0.95rem", fontWeight: 700, color: preset.preview.cardTextColor }}
              >
                111
              </SoftTypography>
            </SoftBox>
          </SoftBox>
        </SoftBox>

        <SoftBox sx={{ display: "flex", alignItems: "center", gap: 0.8, mt: 1.25 }}>
          <SoftBox
            sx={{
              width: 14,
              height: 14,
              borderRadius: 0.6,
              border: `1px solid ${preset.preview.optionBorderColor}`,
              background: preset.preview.optionBackground,
            }}
          />
          <SoftTypography sx={{ fontSize: "0.86rem", color: textMuted }}>
            {previewText.saveCard}
          </SoftTypography>
        </SoftBox>

        <SoftBox
          sx={{
            height: 44,
            mt: 1.4,
            borderRadius: "999px",
            background: preset.preview.buttonBackground,
            color: preset.preview.buttonTextColor,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontWeight: 700,
            fontSize: "0.95rem",
            border: `1px solid ${preset.preview.optionActiveBorderColor}`,
          }}
        >
          {previewText.cta}
        </SoftBox>

        <Divider
          sx={{ my: 1.6, borderColor: preset.preview.dividerColor || "rgba(255,255,255,0.2)" }}
        />

        <SoftBox sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}>
          {[
            { label: "Verified", value: "Secure", icon: ShieldOutlinedIcon },
            { label: "64-BIT", value: "Encrypted", icon: FlagOutlinedIcon },
            { label: "PCS DSS", value: "Compliant", icon: StarOutlineRoundedIcon },
          ].map((badge) => {
            const BadgeIcon = badge.icon;
            return (
              <SoftBox
                key={badge.label}
                sx={{ display: "flex", alignItems: "center", gap: 0.6, minWidth: 0 }}
              >
                <SoftBox
                  sx={{
                    width: 24,
                    height: 24,
                    borderRadius: "50%",
                    background: preset.preview.badgeBackground,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <BadgeIcon sx={{ fontSize: "0.95rem", color: preset.preview.badgeColor }} />
                </SoftBox>
                <SoftBox sx={{ minWidth: 0 }}>
                  <SoftTypography sx={{ fontSize: "0.62rem", color: textMuted, lineHeight: 1.1 }}>
                    {badge.label}
                  </SoftTypography>
                  <SoftTypography
                    sx={{
                      fontSize: "0.75rem",
                      color: preset.preview.textColor,
                      fontWeight: 700,
                      lineHeight: 1.2,
                    }}
                  >
                    {badge.value}
                  </SoftTypography>
                </SoftBox>
              </SoftBox>
            );
          })}
        </SoftBox>

        <SoftBox
          sx={{
            mt: 1.3,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 0.8,
          }}
        >
          <SoftTypography sx={{ fontSize: "0.8rem", color: preset.preview.textColor }}>
            {previewText.poweredBy}
          </SoftTypography>
          <SoftBox
            component="img"
            src={poweredByLogoSrc}
            alt="CyberPay"
            sx={{ width: 88, height: 22, objectFit: "contain" }}
          />
        </SoftBox>
      </SoftBox>
    </SoftBox>
  );
}

PresetPreview.propTypes = {
  preset: PropTypes.shape({
    id: PropTypes.string,
    label: PropTypes.string,
    preview: PropTypes.object,
  }),
  brandLogoUrl: PropTypes.string,
};

function ColorAndPreview({ selectedPresetId, brandLogoUrl, brand, onPresetChange }) {
  const selectedPreset =
    cashierThemePresets.find((preset) => preset.id === selectedPresetId) || cashierThemePresets[0];
  const presetOptions = cashierThemePresets.map((preset) => ({
    label: preset.label,
    value: preset.id,
  }));
  const selectedPresetOption = presetOptions.find((option) => option.value === selectedPreset.id);
  const previewCanvasBackground =
    selectedPreset.preview.canvasBackground || selectedPreset.preview.appBackground;
  const previewCanvasImageUrl = selectedPreset.preview.canvasImageUrl || null;
  const previewCanvasImageOpacity = selectedPreset.preview.canvasImageOpacity || 0;
  const isLime = selectedPreset.id === "lime";

  return (
    <SoftBox mt={{ xs: 2, lg: 0 }}>
      <SoftBox
        sx={{
          display: "grid",
          gridTemplateColumns: isLime
            ? { xs: "1fr", lg: "280px minmax(0, 1fr)" }
            : { xs: "1fr", md: "1fr 1fr" },
          gap: 2.5,
          alignItems: "stretch",
        }}
      >
        <SoftBox
          sx={{
            display: "flex",
            justifyContent: "left",
            alignItems: "flex-start",
            minHeight: isLime ? "auto" : { xs: "auto", md: 360 },
            pt: { xs: 0, md: 0.5 },
          }}
        >
          <SoftBox sx={{ width: "100%", maxWidth: 280 }}>
            <SoftTypography
              variant="button"
              fontWeight="regular"
              color="text"
              textTransform="uppercase"
              textAlign="center"
            ></SoftTypography>
            <SoftBox mt={1}>
              <SoftSelect
                options={presetOptions}
                value={selectedPresetOption}
                onChange={(option) => {
                  if (option?.value) {
                    onPresetChange(option.value);
                  }
                }}
              />
            </SoftBox>
            <SoftTypography
              variant="caption"
              color="text"
              sx={{ opacity: 0.75, mt: 1, display: "block", textAlign: "center" }}
            >
              {selectedPreset.description}
            </SoftTypography>
          </SoftBox>
        </SoftBox>
        <SoftBox
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            minHeight: { xs: "auto", md: 360 },
            width: "100%",
          }}
        >
          <SoftTypography
            variant="caption"
            color="text"
            sx={{ opacity: 0.65, display: "block", textAlign: "center", mb: 1 }}
          >
            Live preview
          </SoftTypography>
          {isLime ? (
            <LimeCashierPreview brand={brand} brandLogoUrl={normalizeLogoUrl(brandLogoUrl)} />
          ) : (
            <SoftBox
              sx={{
                width: "100%",
                maxWidth: 520,
                minHeight: { xs: 520, md: 640 },
                borderRadius: 3,
                position: "relative",
                overflow: "hidden",
                p: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: previewCanvasBackground,
              }}
            >
              {previewCanvasImageUrl && (
                <SoftBox
                  component="img"
                  src={previewCanvasImageUrl}
                  alt={`${selectedPreset.label} canvas background`}
                  sx={{
                    position: "absolute",
                    inset: 0,
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    opacity: previewCanvasImageOpacity,
                  }}
                />
              )}

              {selectedPreset.id === "lotto" && (
                <>
                  <SoftBox
                    component="img"
                    src="/presets/lotto/decorations/left.svg"
                    alt=""
                    sx={{
                      position: "absolute",
                      left: 0,
                      top: "35%",
                      width: 70,
                      opacity: 0.8,
                    }}
                  />
                  <SoftBox
                    component="img"
                    src="/presets/lotto/decorations/right.svg"
                    alt=""
                    sx={{
                      position: "absolute",
                      right: 0,
                      top: "50%",
                      width: 130,
                      opacity: 0.85,
                    }}
                  />
                </>
              )}

              {selectedPreset.id === "lotto-2" && (
                <SoftBox
                  component="img"
                  src="/presets/lotto/decorations/right-dark.svg"
                  alt=""
                  sx={{
                    position: "absolute",
                    right: 0,
                    top: "50%",
                    width: 130,
                    opacity: 0.9,
                  }}
                />
              )}

              <SoftBox sx={{ position: "relative", zIndex: 1 }}>
                <PresetPreview preset={selectedPreset} brandLogoUrl={brandLogoUrl} />
              </SoftBox>
            </SoftBox>
          )}
        </SoftBox>
      </SoftBox>
    </SoftBox>
  );
}

export default ColorAndPreview;

ColorAndPreview.propTypes = {
  selectedPresetId: PropTypes.string,
  brandLogoUrl: PropTypes.string,
  brand: PropTypes.object,
  onPresetChange: PropTypes.func,
};
